import { DiffViewer } from '@/components/compare/diff-viewer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    compareContentResourceArrays,
    flattenFullBook,
    flattenSection,
    generateComparisonSummary,
    getUniqueSections,
    type ComparisonStatus,
    type ComparisonSummary,
    type ContentResourceComparison,
    type FlattenedContentResource,
} from '@/lib/compare-utils';
import { cn } from '@/lib/utils';
import { getFullBook, getSection } from '@/lib/zybooks-api';
import { BookOpen, FileText, Filter, GitCompare, Loader2 } from 'lucide-react';
import { useState } from 'react';

type CompareMode = 'books' | 'sections';
type FilterStatus = ComparisonStatus | 'all';

interface InputPanelProps {
    label: string;
    onLabelChange: (value: string) => void;
    value: string;
    onValueChange: (value: string) => void;
    mode: CompareMode;
    placeholder: string;
    side: 'left' | 'right';
}

function InputPanel({ label, onLabelChange, value, onValueChange, mode, placeholder, side }: InputPanelProps) {
    const borderColor = side === 'left' ? 'border-l-indigo-500' : 'border-l-teal-500';

    return (
        <Card className={cn('border-l-4', borderColor)}>
            <CardHeader className="pb-3">
                <CardTitle className="text-base">{side === 'left' ? 'Source A' : 'Source B'}</CardTitle>
                <CardDescription>{mode === 'books' ? 'Enter the zybook code' : 'Enter zybook code and section info'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                <div>
                    <Label
                        htmlFor={`${side}-label`}
                        className="text-xs text-zinc-500"
                    >
                        Label (e.g., "Legacy" or "Modern Authoring")
                    </Label>
                    <Input
                        id={`${side}-label`}
                        value={label}
                        onChange={e => onLabelChange(e.target.value)}
                        placeholder={side === 'left' ? 'Legacy Flow' : 'Modern Authoring'}
                        className="mt-1"
                    />
                </div>
                <div>
                    <Label
                        htmlFor={`${side}-value`}
                        className="text-xs text-zinc-500"
                    >
                        {mode === 'books' ? 'Zybook Code' : 'Zybook Code'}
                    </Label>
                    <Input
                        id={`${side}-value`}
                        value={value}
                        onChange={e => onValueChange(e.target.value)}
                        placeholder={placeholder}
                        className="mt-1 font-mono"
                    />
                </div>
            </CardContent>
        </Card>
    );
}

function SummaryStats({ summary }: { summary: ComparisonSummary }) {
    return (
        <div className="grid grid-cols-5 gap-3">
            <div className="rounded-lg border border-zinc-200 bg-white p-3 text-center">
                <div className="text-2xl font-bold text-zinc-800">{summary.total}</div>
                <div className="text-xs text-zinc-500">Total</div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                <div className="text-2xl font-bold text-emerald-600">{summary.matches}</div>
                <div className="text-xs text-emerald-600">Matches</div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                <div className="text-2xl font-bold text-amber-600">{summary.differences}</div>
                <div className="text-xs text-amber-600">Differences</div>
            </div>
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center">
                <div className="text-2xl font-bold text-rose-600">{summary.missingLeft}</div>
                <div className="text-xs text-rose-600">Missing Left</div>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{summary.missingRight}</div>
                <div className="text-xs text-blue-600">Missing Right</div>
            </div>
        </div>
    );
}

export default function CompareBooks() {
    const [mode, setMode] = useState<CompareMode>('books');
    const [leftLabel, setLeftLabel] = useState('Legacy Flow');
    const [rightLabel, setRightLabel] = useState('Modern Authoring');
    const [leftValue, setLeftValue] = useState('');
    const [rightValue, setRightValue] = useState('');
    const [leftChapter, setLeftChapter] = useState('');
    const [leftSection, setLeftSection] = useState('');
    const [rightChapter, setRightChapter] = useState('');
    const [rightSection, setRightSection] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [comparisons, setComparisons] = useState<ContentResourceComparison[] | null>(null);
    const [summary, setSummary] = useState<ComparisonSummary | null>(null);
    const [sections, setSections] = useState<Array<{ chapterNumber: number; sectionNumber: number; title: string }>>([]);
    const [selectedSection, setSelectedSection] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

    const handleCompare = async () => {
        if (!leftValue.trim() || !rightValue.trim()) {
            setError('Please enter values for both sources');
            return;
        }

        setLoading(true);
        setError(null);
        setComparisons(null);
        setSummary(null);

        try {
            let filteredLeft: FlattenedContentResource[];
            let filteredRight: FlattenedContentResource[];

            if (mode === 'sections') {
                // Optimized: only fetch the specific sections requested
                const lChapter = parseInt(leftChapter, 10);
                const lSection = parseInt(leftSection, 10);
                const rChapter = parseInt(rightChapter, 10);
                const rSection = parseInt(rightSection, 10);

                if (isNaN(lChapter) || isNaN(lSection) || isNaN(rChapter) || isNaN(rSection)) {
                    setError('Please enter valid chapter and section numbers for both sources');
                    setLoading(false);
                    return;
                }

                const [leftSectionData, rightSectionData] = await Promise.all([
                    getSection(leftValue.trim(), lChapter, lSection),
                    getSection(rightValue.trim(), rChapter, rSection),
                ]);

                filteredLeft = flattenSection(leftSectionData);
                filteredRight = flattenSection(rightSectionData);
            } else {
                // Full book comparison - fetch all sections
                const [leftBook, rightBook] = await Promise.all([getFullBook(leftValue.trim()), getFullBook(rightValue.trim())]);

                filteredLeft = flattenFullBook(leftBook);
                filteredRight = flattenFullBook(rightBook);
            }

            // Compare the content resources
            const results = compareContentResourceArrays(filteredLeft, filteredRight);
            const summaryStats = generateComparisonSummary(results);

            // Get unique sections for filtering
            const allResources = [...filteredLeft, ...filteredRight];
            const uniqueSections = getUniqueSections(allResources);

            setComparisons(results);
            setSummary(summaryStats);
            setSections(uniqueSections);
            setSelectedSection('all');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while comparing');
        } finally {
            setLoading(false);
        }
    };

    const getFilteredComparisons = () => {
        if (!comparisons) return [];

        let filtered = comparisons;

        // Filter by section if selected
        if (selectedSection !== 'all') {
            const [chapter, section] = selectedSection.split('-').map(Number);
            filtered = filtered.filter(c => c.chapterNumber === chapter && c.sectionNumber === section);
        }

        return filtered;
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100 overflow-auto h-screen">
            <div className="mx-auto max-w-6xl px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="mb-2 flex items-center gap-2">
                        <div className="flex flex-1 items-center justify-center gap-2">
                            <GitCompare className="h-8 w-8 text-indigo-600" />
                            <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Compare Books</h1>
                        </div>
                    </div>
                    <p className="text-center text-zinc-600">
                        Compare content resources between different authoring flows to verify parity
                    </p>
                </div>

                {/* Mode Selection */}
                <Tabs
                    value={mode}
                    onValueChange={v => setMode(v as CompareMode)}
                    className="mb-6"
                >
                    <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
                        <TabsTrigger
                            value="books"
                            className="gap-2"
                        >
                            <BookOpen size={16} />
                            Compare Books
                        </TabsTrigger>
                        <TabsTrigger
                            value="sections"
                            className="gap-2"
                        >
                            <FileText size={16} />
                            Compare Sections
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent
                        value="books"
                        className="mt-6"
                    >
                        <div className="grid gap-4 md:grid-cols-2">
                            <InputPanel
                                label={leftLabel}
                                onLabelChange={setLeftLabel}
                                value={leftValue}
                                onValueChange={setLeftValue}
                                mode="books"
                                placeholder="e.g., LinearAlgebraDev"
                                side="left"
                            />
                            <InputPanel
                                label={rightLabel}
                                onLabelChange={setRightLabel}
                                value={rightValue}
                                onValueChange={setRightValue}
                                mode="books"
                                placeholder="e.g., LinearAlgebraModern"
                                side="right"
                            />
                        </div>
                    </TabsContent>

                    <TabsContent
                        value="sections"
                        className="mt-6"
                    >
                        <div className="grid gap-4 md:grid-cols-2">
                            <Card className="border-l-4 border-l-indigo-500">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">Source A</CardTitle>
                                    <CardDescription>Enter zybook code and section to compare</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div>
                                        <Label className="text-xs text-zinc-500">Label</Label>
                                        <Input
                                            value={leftLabel}
                                            onChange={e => setLeftLabel(e.target.value)}
                                            placeholder="Legacy Flow"
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-zinc-500">Zybook Code</Label>
                                        <Input
                                            value={leftValue}
                                            onChange={e => setLeftValue(e.target.value)}
                                            placeholder="e.g., LinearAlgebraDev"
                                            className="mt-1 font-mono"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <Label className="text-xs text-zinc-500">Chapter</Label>
                                            <Input
                                                value={leftChapter}
                                                onChange={e => setLeftChapter(e.target.value)}
                                                placeholder="1"
                                                className="mt-1 font-mono"
                                                type="number"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-zinc-500">Section</Label>
                                            <Input
                                                value={leftSection}
                                                onChange={e => setLeftSection(e.target.value)}
                                                placeholder="1"
                                                className="mt-1 font-mono"
                                                type="number"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-l-4 border-l-teal-500">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">Source B</CardTitle>
                                    <CardDescription>Enter zybook code and section to compare</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div>
                                        <Label className="text-xs text-zinc-500">Label</Label>
                                        <Input
                                            value={rightLabel}
                                            onChange={e => setRightLabel(e.target.value)}
                                            placeholder="Modern Authoring"
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-zinc-500">Zybook Code</Label>
                                        <Input
                                            value={rightValue}
                                            onChange={e => setRightValue(e.target.value)}
                                            placeholder="e.g., LinearAlgebraModern"
                                            className="mt-1 font-mono"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <Label className="text-xs text-zinc-500">Chapter</Label>
                                            <Input
                                                value={rightChapter}
                                                onChange={e => setRightChapter(e.target.value)}
                                                placeholder="1"
                                                className="mt-1 font-mono"
                                                type="number"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-zinc-500">Section</Label>
                                            <Input
                                                value={rightSection}
                                                onChange={e => setRightSection(e.target.value)}
                                                placeholder="1"
                                                className="mt-1 font-mono"
                                                type="number"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>

                {/* Compare Button */}
                <div className="mb-8 flex justify-center">
                    <Button
                        onClick={handleCompare}
                        disabled={loading || !leftValue.trim() || !rightValue.trim()}
                        size="lg"
                        className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Comparing...
                            </>
                        ) : (
                            <>
                                <GitCompare className="h-4 w-4" />
                                Compare Content Resources
                            </>
                        )}
                    </Button>
                </div>

                {/* Error */}
                {error && <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-center text-red-700">{error}</div>}

                {/* Results */}
                {summary && comparisons && (
                    <div className="space-y-6">
                        {/* Summary Stats */}
                        <SummaryStats summary={summary} />

                        {/* Filters */}
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Filter
                                    size={16}
                                    className="text-zinc-500"
                                />
                                <span className="text-sm font-medium text-zinc-700">Filters:</span>
                            </div>

                            <Select
                                value={filterStatus}
                                onValueChange={v => setFilterStatus(v as FilterStatus)}
                            >
                                <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    <SelectItem value="match">Matches</SelectItem>
                                    <SelectItem value="difference">Differences</SelectItem>
                                    <SelectItem value="missing_left">Missing Left</SelectItem>
                                    <SelectItem value="missing_right">Missing Right</SelectItem>
                                </SelectContent>
                            </Select>

                            {sections.length > 1 && (
                                <Select
                                    value={selectedSection}
                                    onValueChange={setSelectedSection}
                                >
                                    <SelectTrigger className="w-64">
                                        <SelectValue placeholder="Section" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Sections</SelectItem>
                                        {sections.map(s => (
                                            <SelectItem
                                                key={`${s.chapterNumber}-${s.sectionNumber}`}
                                                value={`${s.chapterNumber}-${s.sectionNumber}`}
                                            >
                                                Ch {s.chapterNumber}.{s.sectionNumber}: {s.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        {/* Comparison Labels */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 px-4 py-2 text-center">
                                <span className="font-medium text-indigo-700">{leftLabel}</span>
                                <span className="ml-2 text-sm text-indigo-500">({leftValue})</span>
                            </div>
                            <div className="rounded-lg border-2 border-teal-200 bg-teal-50 px-4 py-2 text-center">
                                <span className="font-medium text-teal-700">{rightLabel}</span>
                                <span className="ml-2 text-sm text-teal-500">({rightValue})</span>
                            </div>
                        </div>

                        {/* Diff Viewer */}
                        <DiffViewer
                            comparisons={getFilteredComparisons()}
                            leftLabel={leftLabel}
                            rightLabel={rightLabel}
                            leftCode={leftValue.trim()}
                            rightCode={rightValue.trim()}
                            filter={filterStatus}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
